/**
 * Price Monitor Page
 * Monitors WooCommerce product prices and auto-disables stock on significant price drops
 */

import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Tabs,
    Tab,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Grid,
    Card,
    CardContent,
    CircularProgress,
    Alert,
    Tooltip,
    Checkbox,
} from '@mui/material';
import {
    PlayArrow as ScanIcon,
    CheckCircle as EnableIcon,
    Cancel as DisableIcon,
    Refresh as RefreshIcon,
    Warning as WarningIcon,
    TrendingDown as TrendingDownIcon,
    MonitorHeart as MonitorIcon,

    Delete as DeleteIcon,
    Edit as EditIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { priceMonitorAPI, MonitoredProduct, PriceDropAlert, ScanHistoryItem } from '../../api/priceMonitor';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div role="tabpanel" hidden={value !== index} {...other}>
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

export default function PriceMonitor() {
    const { enqueueSnackbar } = useSnackbar();
    const [tabValue, setTabValue] = useState(0);
    const [loading, setLoading] = useState(false);
    const [scanning, setScanning] = useState(false);

    // Products state
    const [monitored, setMonitored] = useState<MonitoredProduct[]>([]);
    const [notMonitored, setNotMonitored] = useState<MonitoredProduct[]>([]);
    const [autoDisabled, setAutoDisabled] = useState<MonitoredProduct[]>([]);

    // Alerts state
    const [alerts, setAlerts] = useState<PriceDropAlert[]>([]);
    const [alertsTotal, setAlertsTotal] = useState(0);

    // Scan history state
    const [scanHistory, setScanHistory] = useState<ScanHistoryItem[]>([]);

    // Statistics
    const [stats, setStats] = useState<any>(null);

    // Selection state
    const [selectedMonitored, setSelectedMonitored] = useState<number[]>([]);
    const [selectedNotMonitored, setSelectedNotMonitored] = useState<number[]>([]);
    const [selectedAlerts, setSelectedAlerts] = useState<number[]>([]);
    const [bulkThreshold, setBulkThreshold] = useState(25);

    // Dialogs
    const [enableDialog, setEnableDialog] = useState<{ open: boolean; product: MonitoredProduct | null }>({
        open: false,
        product: null,
    });
    const [threshold, setThreshold] = useState(25);
    const [reEnableDialog, setReEnableDialog] = useState<{ open: boolean; product: MonitoredProduct | null }>({
        open: false,
        product: null,
    });
    const [newStock, setNewStock] = useState(0);
    const [resetBaseline, setResetBaseline] = useState(false);
    const [acknowledgeDialog, setAcknowledgeDialog] = useState<{ open: boolean; alert: PriceDropAlert | null }>({
        open: false,
        alert: null,
    });
    const [resolutionNotes, setResolutionNotes] = useState('');
    const [bulkEnableDialog, setBulkEnableDialog] = useState(false);

    // New Dialogs State
    const [editThresholdDialog, setEditThresholdDialog] = useState<{ open: boolean; product: MonitoredProduct | null }>({
        open: false,
        product: null,
    });
    const [resetBaselineDialog, setResetBaselineDialog] = useState<{ open: boolean; product: MonitoredProduct | null }>({
        open: false,
        product: null,
    });
    const [confirmResetAllDialog, setConfirmResetAllDialog] = useState(false);

    // Search state
    const [monitoredSearch, setMonitoredSearch] = useState('');
    const [notMonitoredSearch, setNotMonitoredSearch] = useState('');

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [productsData, alertsData, historyData, statsData] = await Promise.all([
                priceMonitorAPI.getProducts(),
                priceMonitorAPI.getAlerts(1, 50),
                priceMonitorAPI.getScanHistory(1, 50),
                priceMonitorAPI.getStatistics(),
            ]);

            setMonitored(productsData?.monitored || []);
            setNotMonitored(productsData?.not_monitored || []);
            setAutoDisabled(productsData?.auto_disabled || []);
            setAlerts(alertsData?.items || []);
            setAlertsTotal(alertsData?.total || 0);
            setScanHistory(historyData?.items || []);
            setStats(statsData || null);
        } catch (error: any) {
            console.error('Failed to load price monitor data:', error);
            enqueueSnackbar(error.response?.data?.detail || 'Failed to load data', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleRunScan = async () => {
        setScanning(true);
        try {
            const result = await priceMonitorAPI.runScan('manual');
            enqueueSnackbar(
                `Scan completed: ${result.products_scanned} scanned, ${result.alerts_triggered} alerts, ${result.products_disabled} disabled`,
                { variant: result.alerts_triggered > 0 ? 'warning' : 'success' }
            );
            loadData();
        } catch (error: any) {
            enqueueSnackbar(error.response?.data?.detail || 'Scan failed', { variant: 'error' });
        } finally {
            setScanning(false);
        }
    };

    const handleEnableMonitoring = async () => {
        if (!enableDialog.product) return;
        try {
            await priceMonitorAPI.enableMonitoring(
                enableDialog.product.product_id,
                enableDialog.product.variation_id,
                threshold
            );
            enqueueSnackbar('Monitoring enabled successfully', { variant: 'success' });
            setEnableDialog({ open: false, product: null });
            loadData();
        } catch (error: any) {
            enqueueSnackbar(error.response?.data?.detail || 'Failed to enable monitoring', { variant: 'error' });
        }
    };

    const handleDisableMonitoring = async (product: MonitoredProduct) => {
        try {
            await priceMonitorAPI.disableMonitoring(product.product_id, product.variation_id);
            enqueueSnackbar('Monitoring disabled successfully', { variant: 'success' });
            loadData();
        } catch (error: any) {
            enqueueSnackbar(error.response?.data?.detail || 'Failed to disable monitoring', { variant: 'error' });
        }
    };

    const handleReEnable = async () => {
        if (!reEnableDialog.product) return;
        try {
            await priceMonitorAPI.reEnableProduct(
                reEnableDialog.product.product_id,
                reEnableDialog.product.variation_id,
                newStock,
                resetBaseline
            );
            enqueueSnackbar('Product re-enabled successfully', { variant: 'success' });
            setReEnableDialog({ open: false, product: null });
            loadData();
        } catch (error: any) {
            enqueueSnackbar(error.response?.data?.detail || 'Failed to re-enable product', { variant: 'error' });
        }
    };

    const handleAcknowledge = async () => {
        if (!acknowledgeDialog.alert) return;
        try {
            await priceMonitorAPI.acknowledgeAlert(acknowledgeDialog.alert.id, resolutionNotes);
            enqueueSnackbar('Alert acknowledged', { variant: 'success' });
            setAcknowledgeDialog({ open: false, alert: null });
            setResolutionNotes('');
            loadData();
        } catch (error: any) {
            enqueueSnackbar(error.response?.data?.detail || 'Failed to acknowledge alert', { variant: 'error' });
        }
    };

    const handleBulkEnable = async () => {
        if (selectedNotMonitored.length === 0) {
            enqueueSnackbar('No products selected', { variant: 'warning' });
            return;
        }

        try {
            const productIds = selectedNotMonitored.map(id => {
                const product = notMonitored.find(p => p.id === id);
                return {
                    product_id: product!.product_id,
                    variation_id: product!.variation_id
                };
            });

            await priceMonitorAPI.bulkEnableMonitoring(productIds, bulkThreshold);
            enqueueSnackbar(`Enabled monitoring for ${selectedNotMonitored.length} products`, { variant: 'success' });
            setSelectedNotMonitored([]);
            setBulkEnableDialog(false);
            loadData();
        } catch (error: any) {
            enqueueSnackbar(error.response?.data?.detail || 'Failed to enable monitoring', { variant: 'error' });
        }
    };

    const handleBulkDisable = async () => {
        if (selectedMonitored.length === 0) {
            enqueueSnackbar('No products selected', { variant: 'warning' });
            return;
        }

        try {
            const productIds = selectedMonitored.map(id => {
                const product = monitored.find(p => p.id === id);
                return {
                    product_id: product!.product_id,
                    variation_id: product!.variation_id
                };
            });

            await priceMonitorAPI.bulkDisableMonitoring(productIds);
            enqueueSnackbar(`Disabled monitoring for ${selectedMonitored.length} products`, { variant: 'success' });
            setSelectedMonitored([]);
            loadData();
        } catch (error: any) {
            enqueueSnackbar(error.response?.data?.detail || 'Failed to disable monitoring', { variant: 'error' });
        }
    };

    const handleBulkDeleteAlerts = async () => {
        if (selectedAlerts.length === 0) {
            enqueueSnackbar('No alerts selected', { variant: 'warning' });
            return;
        }

        if (!window.confirm(`Are you sure you want to delete ${selectedAlerts.length} alerts? This cannot be undone.`)) {
            return;
        }

        try {
            await priceMonitorAPI.bulkDeleteAlerts(selectedAlerts);
            enqueueSnackbar(`Deleted ${selectedAlerts.length} alerts`, { variant: 'success' });
            setSelectedAlerts([]);
            loadData();
        } catch (error: any) {
            enqueueSnackbar(error.response?.data?.detail || 'Failed to delete alerts', { variant: 'error' });
        }
    };

    const handleUpdateThreshold = async () => {
        if (!editThresholdDialog.product) return;
        try {
            await priceMonitorAPI.updateThreshold(
                editThresholdDialog.product.product_id,
                editThresholdDialog.product.variation_id,
                threshold
            );
            enqueueSnackbar('Threshold updated successfully', { variant: 'success' });
            setEditThresholdDialog({ open: false, product: null });
            loadData();
        } catch (error: any) {
            enqueueSnackbar(error.response?.data?.detail || 'Failed to update threshold', { variant: 'error' });
        }
    };

    const handleResetBaseline = async () => {
        if (!resetBaselineDialog.product) return;
        try {
            await priceMonitorAPI.resetBaseline(
                resetBaselineDialog.product.product_id,
                resetBaselineDialog.product.variation_id
            );
            enqueueSnackbar('Baseline reset successfully', { variant: 'success' });
            setResetBaselineDialog({ open: false, product: null });
            loadData();
        } catch (error: any) {
            enqueueSnackbar(error.response?.data?.detail || 'Failed to reset baseline', { variant: 'error' });
        }
    };

    const handleResetAllBaselines = async () => {
        try {
            const result = await priceMonitorAPI.resetAllBaselines();
            enqueueSnackbar(`${result.message}`, { variant: 'success' });
            setConfirmResetAllDialog(false);
            loadData();
        } catch (error: any) {
            enqueueSnackbar(error.response?.data?.detail || 'Failed to reset baselines', { variant: 'error' });
        }
    };

    const handleSelectAll = (tab: 'monitored' | 'notMonitored' | 'alerts', checked: boolean) => {
        if (tab === 'monitored') {
            setSelectedMonitored(checked ? monitored.map(p => p.id) : []);
        } else if (tab === 'notMonitored') {
            setSelectedNotMonitored(checked ? notMonitored.map(p => p.id) : []);
        } else if (tab === 'alerts') {
            setSelectedAlerts(checked ? alerts.map(a => a.id) : []);
        }
    };

    const handleToggleSelection = (tab: 'monitored' | 'notMonitored' | 'alerts', id: number) => {
        if (tab === 'monitored') {
            setSelectedMonitored(prev =>
                prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
            );
        } else if (tab === 'notMonitored') {
            setSelectedNotMonitored(prev =>
                prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
            );
        } else if (tab === 'alerts') {
            setSelectedAlerts(prev =>
                prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
            );
        }
    };

    const formatPrice = (price: number | string | null) => {
        if (price === null || price === undefined) return 'N/A';
        const numPrice = typeof price === 'string' ? parseFloat(price) : price;
        if (isNaN(numPrice)) return 'N/A';
        return `₹${numPrice.toFixed(2)}`;
    };

    const formatDate = (date: string | null) => {
        if (!date) return 'N/A';
        return new Date(date).toLocaleString();
    };

    if (loading && !stats) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4">
                    <MonitorIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Price Monitor
                </Typography>
                <Box>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={loadData}
                        disabled={loading}
                        sx={{ mr: 2 }}
                    >
                        Refresh
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={() => setConfirmResetAllDialog(true)}
                        disabled={loading || monitored.length === 0}
                        sx={{ mr: 2 }}
                    >
                        Reset All Baselines
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={scanning ? <CircularProgress size={20} color="inherit" /> : <ScanIcon />}
                        onClick={handleRunScan}
                        disabled={scanning}
                    >
                        {scanning ? 'Scanning...' : 'Run Scan'}
                    </Button>
                </Box>
            </Box>

            {/* Statistics Cards */}
            {stats && (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent>
                                <Typography color="textSecondary" gutterBottom>
                                    Total Products
                                </Typography>
                                <Typography variant="h4">{stats.total_products}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card>
                            <CardContent>
                                <Typography color="textSecondary" gutterBottom>
                                    Monitored
                                </Typography>
                                <Typography variant="h4" color="primary">
                                    {stats.monitored_products}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={{ bgcolor: 'error.light' }}>
                            <CardContent>
                                <Typography color="white" gutterBottom>
                                    Auto-Disabled
                                </Typography>
                                <Typography variant="h4" color="white">
                                    {stats.auto_disabled_products}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Card sx={{ bgcolor: 'warning.light' }}>
                            <CardContent>
                                <Typography color="white" gutterBottom>
                                    Active Alerts
                                </Typography>
                                <Typography variant="h4" color="white">
                                    {stats.active_alerts}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {stats?.last_scan_time && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Last scan: {formatDate(stats.last_scan_time)} - Status: {stats.last_scan_status}
                </Alert>
            )}

            <Paper>
                <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
                    <Tab label={`Monitored (${monitored.length})`} />
                    <Tab label={`Not Monitored (${notMonitored.length})`} />
                    <Tab label={`Auto-Disabled (${autoDisabled.length})`} />
                    <Tab label={`Alerts (${alertsTotal})`} />
                    <Tab label="Scan History" />
                </Tabs>

                {/* Monitored Products */}
                <TabPanel value={tabValue} index={0}>
                    {/* Search Box */}
                    <Box sx={{ mb: 2 }}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search by product name or SKU..."
                            value={monitoredSearch}
                            onChange={(e) => setMonitoredSearch(e.target.value)}
                        />
                    </Box>

                    {selectedMonitored.length > 0 && (
                        <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                            <Typography variant="body2">
                                {selectedMonitored.length} selected
                            </Typography>
                            <Button
                                variant="contained"
                                color="error"
                                size="small"
                                onClick={handleBulkDisable}
                            >
                                Disable Monitoring
                            </Button>
                        </Box>
                    )}
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={monitored.length > 0 && selectedMonitored.length === monitored.length}
                                            indeterminate={selectedMonitored.length > 0 && selectedMonitored.length < monitored.length}
                                            onChange={(e) => handleSelectAll('monitored', e.target.checked)}
                                        />
                                    </TableCell>
                                    <TableCell>Product</TableCell>
                                    <TableCell>SKU</TableCell>
                                    <TableCell>Baseline Price</TableCell>
                                    <TableCell>Current Price</TableCell>
                                    <TableCell>Threshold</TableCell>
                                    <TableCell>Stock</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {monitored
                                    .filter(product => {
                                        if (!monitoredSearch) return true;
                                        const search = monitoredSearch.toLowerCase();
                                        const name = (product.parent_product ? `${product.parent_product} - ` : '') + product.product_name;
                                        return name.toLowerCase().includes(search) ||
                                            (product.sku && product.sku.toLowerCase().includes(search));
                                    })
                                    .map((product) => (
                                        <TableRow key={product.id}>
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    checked={selectedMonitored.includes(product.id)}
                                                    onChange={() => handleToggleSelection('monitored', product.id)}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {product.parent_product && `${product.parent_product} - `}
                                                {product.product_name}
                                            </TableCell>
                                            <TableCell>{product.sku || 'N/A'}</TableCell>
                                            <TableCell>
                                                {formatPrice(product.baseline_regular_price)}
                                                {product.baseline_sale_price && (
                                                    <> / {formatPrice(product.baseline_sale_price)}</>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {formatPrice(product.current_regular_price)}
                                                {product.current_sale_price && (
                                                    <> / {formatPrice(product.current_sale_price)}</>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <Chip label={`${product.threshold_percentage}%`} size="small" sx={{ mr: 1 }} />
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => {
                                                            setEditThresholdDialog({ open: true, product });
                                                            setThreshold(product.threshold_percentage || 25);
                                                        }}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </TableCell>
                                            <TableCell>{product.current_stock_quantity}</TableCell>
                                            <TableCell>
                                                <Tooltip title="Reset Baseline to Current Price">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setResetBaselineDialog({ open: true, product })}
                                                        color="primary"
                                                    >
                                                        <RefreshIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Disable Monitoring">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleDisableMonitoring(product)}
                                                    >
                                                        <DisableIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </TabPanel>

                {/* Not Monitored Products */}
                <TabPanel value={tabValue} index={1}>
                    {/* Search Box */}
                    <Box sx={{ mb: 2 }}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search by product name or SKU..."
                            value={notMonitoredSearch}
                            onChange={(e) => setNotMonitoredSearch(e.target.value)}
                        />
                    </Box>

                    {selectedNotMonitored.length > 0 && (
                        <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                            <Typography variant="body2">
                                {selectedNotMonitored.length} selected
                            </Typography>
                            <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                onClick={() => setBulkEnableDialog(true)}
                            >
                                Enable Monitoring
                            </Button>
                        </Box>
                    )}
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={notMonitored.length > 0 && selectedNotMonitored.length === notMonitored.length}
                                            indeterminate={selectedNotMonitored.length > 0 && selectedNotMonitored.length < notMonitored.length}
                                            onChange={(e) => handleSelectAll('notMonitored', e.target.checked)}
                                        />
                                    </TableCell>
                                    <TableCell>Product</TableCell>
                                    <TableCell>SKU</TableCell>
                                    <TableCell>Current Price</TableCell>
                                    <TableCell>Stock</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {notMonitored
                                    .filter(product => {
                                        if (!notMonitoredSearch) return true;
                                        const search = notMonitoredSearch.toLowerCase();
                                        const name = (product.parent_product ? `${product.parent_product} - ` : '') + product.product_name;
                                        return name.toLowerCase().includes(search) ||
                                            (product.sku && product.sku.toLowerCase().includes(search));
                                    })
                                    .map((product) => (
                                        <TableRow key={product.id}>
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    checked={selectedNotMonitored.includes(product.id)}
                                                    onChange={() => handleToggleSelection('notMonitored', product.id)}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {product.parent_product && `${product.parent_product} - `}
                                                {product.product_name}
                                            </TableCell>
                                            <TableCell>{product.sku || 'N/A'}</TableCell>
                                            <TableCell>
                                                {formatPrice(product.current_regular_price)}
                                                {product.current_sale_price && (
                                                    <> / {formatPrice(product.current_sale_price)}</>
                                                )}
                                            </TableCell>
                                            <TableCell>{product.current_stock_quantity}</TableCell>
                                            <TableCell>
                                                <Tooltip title="Enable Monitoring">
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => {
                                                            setEnableDialog({ open: true, product });
                                                            setThreshold(25);
                                                        }}
                                                    >
                                                        <EnableIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </TabPanel>

                {/* Auto-Disabled Products */}
                <TabPanel value={tabValue} index={2}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Product</TableCell>
                                    <TableCell>SKU</TableCell>
                                    <TableCell>Disabled At</TableCell>
                                    <TableCell>Reason</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {autoDisabled.map((product) => (
                                    <TableRow key={product.id}>
                                        <TableCell>
                                            {product.parent_product && `${product.parent_product} - `}
                                            {product.product_name}
                                        </TableCell>
                                        <TableCell>{product.sku || 'N/A'}</TableCell>
                                        <TableCell>{formatDate(product.auto_disabled_at)}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={product.auto_disabled_reason}
                                                color="error"
                                                size="small"
                                                icon={<TrendingDownIcon />}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => {
                                                    setReEnableDialog({ open: true, product });
                                                    setNewStock(0);
                                                    setResetBaseline(false);
                                                }}
                                            >
                                                Re-Enable
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </TabPanel>

                {/* Alerts */}
                <TabPanel value={tabValue} index={3}>
                    {selectedAlerts.length > 0 && (
                        <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                            <Typography variant="body2">
                                {selectedAlerts.length} selected
                            </Typography>
                            <Button
                                variant="contained"
                                color="error"
                                size="small"
                                startIcon={<DeleteIcon />}
                                onClick={handleBulkDeleteAlerts}
                            >
                                Delete Selected
                            </Button>
                        </Box>
                    )}
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={alerts.length > 0 && selectedAlerts.length === alerts.length}
                                            indeterminate={selectedAlerts.length > 0 && selectedAlerts.length < alerts.length}
                                            onChange={(e) => handleSelectAll('alerts', e.target.checked)}
                                        />
                                    </TableCell>
                                    <TableCell>Product</TableCell>
                                    <TableCell>Triggered By</TableCell>
                                    <TableCell>Price Drop</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Created</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {alerts.map((alert) => (
                                    <TableRow key={alert.id}>
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                checked={selectedAlerts.includes(alert.id)}
                                                onChange={() => handleToggleSelection('alerts', alert.id)}
                                            />
                                        </TableCell>
                                        <TableCell>{alert.product_name}</TableCell>
                                        <TableCell>
                                            <Chip label={alert.triggered_by} size="small" />
                                        </TableCell>
                                        <TableCell>
                                            <Typography color="error">
                                                {alert.triggered_by === 'regular_price'
                                                    ? `${alert.regular_price_drop_percentage}%`
                                                    : `${alert.sale_price_drop_percentage}%`}
                                            </Typography>
                                            <Typography variant="caption">
                                                {formatPrice(
                                                    alert.triggered_by === 'regular_price'
                                                        ? alert.baseline_regular_price
                                                        : alert.baseline_sale_price
                                                )}{' '}
                                                →{' '}
                                                {formatPrice(
                                                    alert.triggered_by === 'regular_price'
                                                        ? alert.detected_regular_price
                                                        : alert.detected_sale_price
                                                )}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={alert.alert_status}
                                                color={alert.alert_status === 'active' ? 'error' : 'default'}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>{formatDate(alert.created_at)}</TableCell>
                                        <TableCell>
                                            {alert.alert_status === 'active' && (
                                                <Button
                                                    size="small"
                                                    onClick={() => {
                                                        setAcknowledgeDialog({ open: true, alert });
                                                        setResolutionNotes('');
                                                    }}
                                                >
                                                    Acknowledge
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </TabPanel>

                {/* Scan History */}
                <TabPanel value={tabValue} index={4}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Scan Type</TableCell>
                                    <TableCell>Products Scanned</TableCell>
                                    <TableCell>Alerts</TableCell>
                                    <TableCell>Disabled</TableCell>
                                    <TableCell>Duration</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Started At</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {scanHistory.map((scan) => (
                                    <TableRow key={scan.id}>
                                        <TableCell>
                                            <Chip label={scan.scan_type} size="small" />
                                        </TableCell>
                                        <TableCell>{scan.products_scanned}</TableCell>
                                        <TableCell>
                                            {scan.alerts_triggered > 0 ? (
                                                <Chip
                                                    label={scan.alerts_triggered}
                                                    color="warning"
                                                    size="small"
                                                    icon={<WarningIcon />}
                                                />
                                            ) : (
                                                scan.alerts_triggered
                                            )}
                                        </TableCell>
                                        <TableCell>{scan.products_disabled}</TableCell>
                                        <TableCell>{scan.duration_seconds}s</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={scan.scan_status}
                                                color={scan.scan_status === 'completed' ? 'success' : 'error'}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>{formatDate(scan.started_at)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </TabPanel>
            </Paper>

            {/* Enable Monitoring Dialog */}
            <Dialog open={enableDialog.open} onClose={() => setEnableDialog({ open: false, product: null })}>
                <DialogTitle>Enable Price Monitoring</DialogTitle>
                <DialogContent>
                    <Typography gutterBottom>
                        Product: {enableDialog.product?.product_name}
                    </Typography>
                    <TextField
                        fullWidth
                        label="Threshold Percentage"
                        type="number"
                        value={threshold}
                        onChange={(e) => setThreshold(Number(e.target.value))}
                        inputProps={{ min: 1, max: 100 }}
                        helperText="Alert will trigger if price drops by this percentage or more"
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEnableDialog({ open: false, product: null })}>Cancel</Button>
                    <Button onClick={handleEnableMonitoring} variant="contained">
                        Enable
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Threshold Dialog */}
            <Dialog open={editThresholdDialog.open} onClose={() => setEditThresholdDialog({ open: false, product: null })}>
                <DialogTitle>Edit Threshold</DialogTitle>
                <DialogContent>
                    <Typography gutterBottom>
                        Update the price drop threshold percentage for {editThresholdDialog.product?.product_name}.
                    </Typography>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Threshold Percentage"
                        type="number"
                        fullWidth
                        value={threshold}
                        onChange={(e) => setThreshold(Number(e.target.value))}
                        inputProps={{ min: 1, max: 100 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditThresholdDialog({ open: false, product: null })}>Cancel</Button>
                    <Button onClick={handleUpdateThreshold} variant="contained" color="primary">
                        Update
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Reset Baseline Dialog */}
            <Dialog open={resetBaselineDialog.open} onClose={() => setResetBaselineDialog({ open: false, product: null })}>
                <DialogTitle>Reset Baseline?</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to reset the baseline for <b>{resetBaselineDialog.product?.product_name}</b>?
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                        The baseline will be updated to the <b>current price</b>. This will clear any potential drop alerts based on the old baseline.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setResetBaselineDialog({ open: false, product: null })}>Cancel</Button>
                    <Button onClick={handleResetBaseline} variant="contained" color="primary">
                        Reset Baseline
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirm Reset All Dialog */}
            <Dialog open={confirmResetAllDialog} onClose={() => setConfirmResetAllDialog(false)}>
                <DialogTitle>Reset All Baselines?</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        This will reset the baseline for <b>{monitored.length} products</b> to their current prices.
                    </Alert>
                    <Typography>
                        Use this if you have legitimately updated many prices and want to prevent "false alarms" or "creeping drop" alerts.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmResetAllDialog(false)}>Cancel</Button>
                    <Button onClick={handleResetAllBaselines} variant="contained" color="warning">
                        Reset All
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Re-Enable Product Dialog */}
            <Dialog open={reEnableDialog.open} onClose={() => setReEnableDialog({ open: false, product: null })}>
                <DialogTitle>Re-Enable Product</DialogTitle>
                <DialogContent>
                    <Typography gutterBottom>
                        Product: {reEnableDialog.product?.product_name}
                    </Typography>
                    <TextField
                        fullWidth
                        label="New Stock Quantity"
                        type="number"
                        value={newStock}
                        onChange={(e) => setNewStock(Number(e.target.value))}
                        inputProps={{ min: 0 }}
                        sx={{ mt: 2 }}
                    />
                    <Box sx={{ mt: 2 }}>
                        <label>
                            <input
                                type="checkbox"
                                checked={resetBaseline}
                                onChange={(e) => setResetBaseline(e.target.checked)}
                            />
                            {' '}Reset baseline to current prices
                        </label>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setReEnableDialog({ open: false, product: null })}>Cancel</Button>
                    <Button onClick={handleReEnable} variant="contained">
                        Re-Enable
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Acknowledge Alert Dialog */}
            <Dialog open={acknowledgeDialog.open} onClose={() => setAcknowledgeDialog({ open: false, alert: null })}>
                <DialogTitle>Acknowledge Alert</DialogTitle>
                <DialogContent>
                    <Typography gutterBottom>
                        Product: {acknowledgeDialog.alert?.product_name}
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Resolution Notes"
                        value={resolutionNotes}
                        onChange={(e) => setResolutionNotes(e.target.value)}
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAcknowledgeDialog({ open: false, alert: null })}>Cancel</Button>
                    <Button onClick={handleAcknowledge} variant="contained">
                        Acknowledge
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Bulk Enable Dialog */}
            <Dialog open={bulkEnableDialog} onClose={() => setBulkEnableDialog(false)}>
                <DialogTitle>Enable Monitoring for {selectedNotMonitored.length} Products</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Threshold Percentage"
                        type="number"
                        value={bulkThreshold}
                        onChange={(e) => setBulkThreshold(Number(e.target.value))}
                        inputProps={{ min: 1, max: 100 }}
                        helperText="Alert will trigger if price drops by this percentage or more"
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBulkEnableDialog(false)}>Cancel</Button>
                    <Button onClick={handleBulkEnable} variant="contained">
                        Enable Monitoring
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
